/**
 * Chat.tsx
 *
 * KI-Assistent-Chat-Komponente für den Zeitgeist-Regeleditor.
 *
 * Nimmt natürlichsprachliche Anweisungen des Benutzers entgegen,
 * schickt sie zusammen mit dem aktuellen rules_xml an den Backend-Endpunkt
 * POST /api/projects/rules/assist und zeigt die Antwort als Chat-Nachricht an.
 *
 * Bei mode="mutate" wird das geänderte XML via `onXmlReceived` nach oben
 * weitergegeben, damit der Editor in Administration.tsx es übernehmen kann.
 *
 * Props:
 *   user          – Aktuell eingeloggter Benutzer (für API-Authentifizierung)
 *   project       – Aktives Projekt (ID wird an API übergeben)
 *   mode          – Theme-Modus ("dark" / "light") für CSS-Klassen
 *   xmlText       – Aktuelles rules_xml aus dem Editor
 *   onXmlReceived – Callback: wird mit dem geänderten XML aufgerufen wenn
 *                   das LLM eine Mutation durchgeführt hat
 */
import React, { useState, useRef, useEffect } from "react";
import { AssistMessage, assistRules } from "./api/AssistClient";
import { UserInfo, ProjectDetail } from "./model/backendTypes";
import iconDark from "./landing/assets/icon-dark.png";

/** Internes Nachrichtenformat für die Chat-Anzeige (user/assistant). */
type ChatMessage = {
  role: "user" | "assistant";
  content: string;
};

type StarterPrompt = {
  id: string;
  label: string;
  prompt: string;
};

const starterPrompts: StarterPrompt[] = [
  {
    id: "learn",
    label: "Learn more about zeitgeist",
    prompt: "Learn more about zeitgeist",
  },
  {
    id: "new-project",
    label: "Start a new project",
    prompt: "Start a new project",
  },
  {
    id: "feedback",
    label: "Get feedback on your rules",
    prompt: "Get feedback on your rules",
  },
  {
    id: "other",
    label: "Something else..",
    prompt: "I need help with something else.",
  },
];

/** Gibt value.trim() zurück wenn es ein String ist, sonst "". */
const normalizeText = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";


const buildAssistantDisplayText = (reply: Record<string, unknown>): string =>
  normalizeText(reply?.text);

/** Props der Chat-Komponente. */
type ChatProps = {
  user: UserInfo | null;
  project: ProjectDetail | null;
  /** Theme-Modus für CSS-Klassen: "dark" oder "light" */
  mode: string;
  /** Aktuelles rules_xml aus dem Editor – wird bei jedem Aufruf mitgeschickt */
  xmlText: string;
  /** Wird aufgerufen wenn das LLM das XML geändert hat (mode="mutate") */
  onXmlReceived: (xml: string) => void;
};

/** localStorage-Schlüssel für den Chat-Verlauf eines bestimmten Projekts. */
const chatStorageKey = (projectId: number) => `zeitgeist_chat_${projectId}`;

/** Löscht den gespeicherten Chat-Verlauf für ein Projekt aus dem localStorage.
 *  Sollte nach dem Löschen eines Projekts aufgerufen werden. */
export function clearChatStorageForProject(projectId: number): void {
  try {
    localStorage.removeItem(chatStorageKey(projectId));
  } catch {
    // localStorage nicht verfügbar – ignorieren
  }
}

/** Lädt den gespeicherten Chat-Verlauf aus localStorage für ein Projekt. */
const loadChatFromStorage = (projectId: number) => {
  try {
    const raw = localStorage.getItem(chatStorageKey(projectId));
    if (!raw) return null;
    const data = JSON.parse(raw);
    return {
      messages: (data.messages ?? []) as ChatMessage[],
      history: (data.history ?? []) as AssistMessage[],
      state: (data.state ?? {}) as Record<string, unknown>,
    };
  } catch {
    return null;
  }
};

export default function Chat({
  user,
  project,
  mode,
  xmlText,
  onXmlReceived,
}: ChatProps) {
  const chatAvatarSrc = mode === "dark" ? iconDark : "/favicon-32.png";

  // Anzeigemessages im Chat-Fenster (user + assistant, nur für UI)
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  // Lokal gespeicherter Verlauf im API-Format; das Backend kürzt für den Modellaufruf.
  const [chatHistory, setChatHistory] = useState<AssistMessage[]>([]);
  // Aktueller Eingabetext im Textfeld
  const [chatInput, setChatInput] = useState<string>("");
  // Fehlermeldung aus dem letzten API-Aufruf (leer = kein Fehler)
  const [chatError, setChatError] = useState<string>("");
  // true während ein API-Aufruf läuft → Eingabe und Senden-Button gesperrt
  const [chatBusy, setChatBusy] = useState<boolean>(false);
  // Gesprächszustand: wird pro Antwort aus context_update aktualisiert
  // (z.B. active_resource, active_day, simultaneous)
  const [conversationState, setConversationState] = useState<Record<string, unknown>>({});
  // Ref auf die Nachrichten-Liste für Auto-Scroll nach unten
  const chatMessagesRef = useRef<HTMLDivElement | null>(null);
  // Flag: true sobald der initiale Lade-Effekt abgeschlossen ist
  // Verhindert dass der Speicher-Effekt den localStorage beim ersten Mount überschreibt
  const initializedRef = useRef<boolean>(false);
  // Zählt wie oft project?.id gewechselt hat — wird benutzt um den
  // Speicher-Effekt erst nach einem vollständigen Render-Zyklus zu aktivieren
  const [loadGeneration, setLoadGeneration] = useState<number>(0);

  /** Setzt alle Chat-Zustände zurück und löscht den localStorage-Eintrag. */
  const resetChat = (projectId?: number) => {
    const id = projectId ?? project?.id;
    if (id) localStorage.removeItem(chatStorageKey(id));
    setChatMessages([]);
    setChatHistory([]);
    setChatInput("");
    setChatError("");
    setConversationState({});
  };

  // Bei Projektwechsel: gespeicherten Verlauf laden, sonst zurücksetzen
  useEffect(() => {
    initializedRef.current = false;
    setChatBusy(false);
    // Remove any UI-visible messages immediately when switching projects
    // to avoid briefly showing the previous project's chat while we load.
    setChatMessages([]);
    setChatHistory([]);
    if (!project?.id) {
      resetChat();
    } else {
      const saved = loadChatFromStorage(project.id);
      if (saved) {
        setChatMessages(saved.messages);
        setChatHistory(saved.history);
        setConversationState(saved.state);
        setChatError("");
      } else {
        resetChat(project.id);
      }
    }
    // Trigger einen neuen Render-Zyklus damit der Speicher-Effekt erst danach aktiv wird
    setLoadGeneration((g) => g + 1);
  }, [project?.id]);

  // Nach dem Laden: initialized-Flag erst im nächsten Render-Zyklus setzen
  useEffect(() => {
    if (loadGeneration > 0) {
      initializedRef.current = true;
    }
  }, [loadGeneration]);

  // Chat-Verlauf in localStorage speichern sobald sich Nachrichten oder Kontext ändern
  // Erst nach dem initialen Laden speichern (initializedRef verhindert Race-Condition)
  useEffect(() => {
    if (!project?.id || !initializedRef.current) return;
    try {
      localStorage.setItem(chatStorageKey(project.id), JSON.stringify({
        messages: chatMessages,
        history: chatHistory,
        state: conversationState,
      }));
    } catch {
      // localStorage voll oder nicht verfügbar – ignorieren
    }
  }, [chatMessages, chatHistory, conversationState]);

  // Nach jeder neuen Nachricht oder laufendem Aufruf: automatisch nach unten scrollen
  useEffect(() => {
    if (!chatMessagesRef.current) return;
    chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
  }, [chatMessages, chatBusy]);

  /**
   * Sendet die aktuelle Eingabe an den Backend-Assistenten.
   *
   * Ablauf:
   *  1. Eingabe validieren (nicht leer, kein laufender Request)
   *  2. User-Nachricht sofort in der UI anzeigen
   *  3. API-Aufruf: assistRules(userId, projectId, { instruction, rules_xml, conversation_state, history })
   *  4. Antworttext aus reply ableiten (buildAssistantDisplayText)
   *  5. Assistent-Nachricht anzeigen
   *  6. Lokale Konversationshistorie begrenzen
   *  7. conversation_state aus context_update aktualisieren
   *  8. Bei mode="mutate": geändertes XML via onXmlReceived weitergeben
   */
  const handleSendChat = async (prefilledInstruction?: string) => {
    if (!user || !project || chatBusy) return;

    const instruction = (prefilledInstruction ?? chatInput).trim();
    if (!instruction) return;

    // Snapshot des aktuellen Verlaufs vor dem neuen Request
    const history: AssistMessage[] = [...chatHistory];

    // User-Nachricht sofort anzeigen und Eingabefeld leeren
    setChatMessages((prev) => [...prev, { role: "user", content: instruction }]);
    setChatInput("");
    setChatError("");
    setChatBusy(true);

    try {
      const result = await assistRules(
        user.id,
        project.id,
        {
          instruction,
          rules_xml: xmlText,           // Aktuelles XML aus dem Editor
          conversation_state: conversationState,  // Bisheriger Gesprächskontext
          history,
        }
      );

      // Anzeigetext aus der strukturierten LLM-Antwort ableiten
      const reply = result.reply as Record<string, unknown> | undefined;
      const replyMode = normalizeText(reply?.mode);

      // Wenn das LLM mode="mutate" sagt, aber das Backend keine echte Änderung
      // festgestellt hat (changed=false), zeigen wir eine ehrliche Meldung.
      let displayText: string;
      if (replyMode === "mutate" && !result.changed) {
        displayText = normalizeText(reply?.text);
      } else {
        displayText = buildAssistantDisplayText((result.reply as Record<string, unknown>) || {});
      }
      setChatMessages((prev) => [
        ...prev,
        { role: "assistant", content: displayText },
      ]);

      // Verlauf lokal begrenzen; der Backend-Assistent nimmt davon nur den kompakten Kontext.
      const newHistory: AssistMessage[] = [
        ...history,
        { role: "user", content: instruction },
        { role: "assistant", content: displayText },
      ];
      setChatHistory(newHistory.slice(-20));

      // conversation_state mit dem vom Backend gelieferten context_update zusammenführen
      const maybeContextUpdate = reply?.context_update;
      if (maybeContextUpdate && typeof maybeContextUpdate === "object") {
        setConversationState({
          ...conversationState,
          ...(maybeContextUpdate as Record<string, unknown>),
        });
      }

      // Bei Regeländerung: geändertes XML an die übergeordnete Komponente übergeben
      // Nur wenn das Backend bestätigt hat, dass sich das XML tatsächlich geändert hat.
      if (result.changed && replyMode === "mutate" && result.xml) {
        onXmlReceived(result.xml);
      }
    } catch (e) {
      console.error("Assistant call failed:", e);
      setChatError(e instanceof Error ? e.message : String(e));
    } finally {
      setChatBusy(false);
    }
  };

  return (
    <div className={`chat-panel ${mode === "dark" ? "theme-dark" : "theme-light"}`}>
      {/* ── Nachrichtenbereich ── */}
      <div className="chat-panel__messages" ref={chatMessagesRef}>
        {/* Leerer Zustand: Begrüßungsnachricht wenn noch keine Nachrichten vorhanden */}
        {chatMessages.length === 0 && (
          <div className="chat-panel__empty">
            <div className="chat-panel__empty-title">Hello! What would you like to do?</div>
            <div className="chat-panel__empty-subtitle">
              <div className="chat-panel__starter-list">
                {starterPrompts.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className="chat-panel__starter-btn"
                    disabled={chatBusy || !user || !project}
                    onClick={() => void handleSendChat(item.prompt)}
                  >
                    <span className="chat-panel__starter-label">{item.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Nachrichten-Liste: user-Nachrichten rechts, assistant-Nachrichten links */}
        {chatMessages.map((msg, index) => (
          <div
            key={`${msg.role}-${index}`}
            className={`chat-panel__row chat-panel__row--${msg.role}`}
          >
            {/* Avatar-Badge nur bei Assistenten-Nachrichten */}
            {msg.role === "assistant" && (
              <div className="chat-panel__badge" title="Zeitgeist">
                <img src={chatAvatarSrc} alt="Zeitgeist Logo" width="20" height="20" className="chat-panel__avatar-img" />
              </div>
            )}
            <div className={`chat-panel__bubble chat-panel__bubble--${msg.role}`}>
              <div className="chat-panel__text">{msg.content}</div>
            </div>
          </div>
        ))}

        {/* Ladeindikator: wird während chatBusy=true angezeigt ("Denkt nach ...") */}
        {chatBusy && (
          <div className="chat-panel__row chat-panel__row--assistant chat-panel__row--thinking">
            <div className="chat-panel__badge" title="Zeitgeist">
              <img src={chatAvatarSrc} alt="Zeitgeist Logo" width="20" height="20" className="chat-panel__avatar-img" />
            </div>
            <div className="chat-panel__bubble chat-panel__bubble--assistant chat-panel__bubble--thinking">
              <div className="chat-panel__thinking">
                <span className="chat-panel__thinking-label">thinking</span>
                <span className="chat-panel__thinking-dots" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Eingabebereich ── */}
      <div className="chat-panel__composer">
        {/* Auto-wachsendes Textarea: Höhe passt sich dem Inhalt an via ref-Callback */}
        <textarea
          value={chatInput}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setChatInput(e.target.value)}
          className="chat-panel__input"
          placeholder="What would you like to do?"
          rows={1}
          onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
            // Enter ohne Shift = Senden; Shift+Enter = Zeilenumbruch
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void handleSendChat();
            }
          }}
          ref={(el) => {
            if (el) {
              el.style.height = "auto";
              el.style.height = `${el.scrollHeight}px`;
            }
          }}
        />
        <div className="chat-panel__actions">
          <div className="chat-panel__actions-left">
            {/* Papierkorb-Button: Verlauf leeren (deaktiviert wenn leer oder busy) */}
            <button
              className="chat-panel__icon-btn"
              onClick={() => resetChat()}
              disabled={chatBusy || chatMessages.length === 0}
              title="Verlauf leeren"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
              </svg>
            </button>
          </div>
          <div className="chat-panel__actions-right">
            {/* Senden-Button: deaktiviert wenn busy, leer oder kein User/Projekt geladen */}
            <button
              className="chat-panel__icon-btn chat-panel__icon-btn--send"
              onClick={() => void handleSendChat()}
              disabled={chatBusy || !chatInput.trim() || !user || !project}
              title="Senden"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                <path d="M2.01 21L23 12L2.01 3L2 10l15 2l-15 2z" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Fehlermeldung: wird angezeigt wenn der letzte API-Aufruf fehlgeschlagen ist */}
      {chatError && (
        <div className="chat-panel__error">
          <strong>Fehler:</strong> {chatError}
        </div>
      )}
    </div>
  );
}
